import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ShieldCheck, Lock } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccessMatrix({ getAuthHeader }) {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [rolesRes, modsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/roles`, getAuthHeader()),
        axios.get(`${API_URL}/admin/modules`, getAuthHeader()),
      ]);
      setRoles(rolesRes.data.filter(r => !['client', 'sales_agent'].includes(r.name)));
      setModules(modsRes.data);
    } catch { toast.error('Failed to load access settings'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggle = async (role, moduleKey) => {
    if (role.name === 'admin' && moduleKey === 'users') return; // locked
    const newModules = { ...(role.modules || {}), [moduleKey]: !role.modules?.[moduleKey] };
    setSaving(`${role.id}-${moduleKey}`);
    // optimistic update
    setRoles(prev => prev.map(r => r.id === role.id ? { ...r, modules: newModules } : r));
    try {
      await axios.put(`${API_URL}/admin/roles/${role.id}`, { modules: newModules }, getAuthHeader());
      toast.success(`${role.label}: ${moduleKey} ${newModules[moduleKey] ? 'shown' : 'hidden'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update access');
      fetchAll();
    } finally { setSaving(null); }
  };

  if (loading) return <p className="text-center text-slate-500 py-8">Loading access settings...</p>;

  return (
    <Card data-testid="access-matrix">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Page Access Control</CardTitle>
        <CardDescription>
          Tick which pages each role can see. Staff only see ticked pages after their next login or page refresh.
          Your own (Admin) row is editable too — Users stays locked on so you can never lock yourself out.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="access-matrix-table">
            <thead>
              <tr>
                <th className="min-w-[140px]">Role</th>
                {modules.map(m => (
                  <th key={m.key} className="text-center text-[11px]" title={m.description}>{m.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roles.map(role => (
                <tr key={role.id} className={role.name === 'admin' ? 'bg-slate-50/70' : ''}>
                  <td>
                    <span className="font-medium">{role.label}</span>
                    {role.is_system && <Badge variant="outline" className="ml-2 text-[10px] bg-slate-100 text-slate-500">system</Badge>}
                  </td>
                  {modules.map(m => {
                    const locked = role.name === 'admin' && m.key === 'users';
                    const checked = !!role.modules?.[m.key];
                    return (
                      <td key={m.key} className="text-center">
                        {locked ? (
                          <span className="inline-flex items-center justify-center" title="Always on for Admin" data-testid="access-cell-admin-users-locked">
                            <Lock className="w-3.5 h-3.5 text-slate-400" />
                          </span>
                        ) : (
                          <input
                            type="checkbox"
                            className="rounded w-4 h-4 accent-[#0f2b4a] cursor-pointer"
                            checked={checked}
                            disabled={saving === `${role.id}-${m.key}`}
                            onChange={() => toggle(role, m.key)}
                            data-testid={`access-cell-${role.name}-${m.key}`}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          Tip: "Financial Figures" hides money stats (portfolio, profit, loan values) from a role without hiding the whole page.
        </p>
      </CardContent>
    </Card>
  );
}
